/**
 * /dashboard — authed home. Greeting + connected-groups/API-key stats +
 * a signpost to MCP connection setup + live import status. Built from
 * available data only —
 * the backend has no messages-indexed or activity-feed source yet, so those
 * render as "coming soon" rather than fabricated numbers.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Activity, ArrowRight, Loader2, MessageSquare, Terminal } from 'lucide-react';
import { EmptyState } from '@/components/patterns/empty-state';
import { IconTile } from '@/components/patterns/icon-tile';
import { PageHeader } from '@/components/patterns/page-header';
import { StatCard } from '@/components/patterns/stat-card';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { connectorApi, type ImportBatch, importsApi, mcpKeysApi, workspaceApi } from '@/lib/api';
import { authClient } from '@/lib/auth-client';
import { useConnectionStatus } from '@/lib/connections';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

function timeOfDayGreeting(hour: number): string {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export function DashboardPage() {
  const { data: session, isPending: sessionLoading } = authClient.useSession();
  const { data: wsData, isLoading: wsLoading } = useQuery({
    queryKey: qk.workspace,
    queryFn: () => workspaceApi.get(),
  });
  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: qk.groups,
    queryFn: () => connectorApi.listGroups(),
  });
  const { data: keysData, isLoading: keysLoading } = useQuery({
    queryKey: qk.mcpKeys,
    queryFn: () => mcpKeysApi.list(),
  });
  // The setup prompt below keys off every connector, not the shared-number
  // group list: a BYO or Signal user IS connected, and sending them back to a
  // wizard that immediately returns them here would be a dead end.
  const { connected, isLoading: connectionLoading } = useConnectionStatus();
  // Poll while any import is running so the status banner stays live.
  const { data: importsData } = useQuery({
    queryKey: qk.imports,
    queryFn: () => importsApi.list(),
    refetchInterval: (query) => {
      const batches = (query.state.data as { batches: ImportBatch[] } | undefined)?.batches ?? [];
      return batches.some((b) => b.status === 'parsing' || b.status === 'indexing') ? 3_000 : false;
    },
  });

  const headerLoading = sessionLoading || wsLoading;
  const statsLoading = groupsLoading || keysLoading;

  const groups = groupsData ?? [];
  const keys = keysData?.keys ?? [];
  const enabledKeyCount = keys.filter((k) => k.enabled).length;
  const lastUsedAt = keys
    .map((k) => k.lastRequest)
    .filter((d): d is string => Boolean(d))
    .sort()
    .at(-1);

  const activeImports = (importsData?.batches ?? []).filter(
    (b) => b.status === 'parsing' || b.status === 'indexing',
  );

  const indexingCount = groups.filter((g) => g.ingestionEnabled).length;
  const pausedCount = groups.length - indexingCount;
  const groupsSub =
    groups.length > 0 ? `${indexingCount} indexing · ${pausedCount} paused` : undefined;
  const keysSub = lastUsedAt ? `Last used ${new Date(lastUsedAt).toLocaleDateString()}` : undefined;

  const firstName = session?.user.name.split(' ')[0] ?? '';
  const workspaceName = wsData?.workspace.name ?? '';

  return (
    <div>
      {headerLoading ? (
        <div className="space-y-2.5">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-80" />
        </div>
      ) : (
        <PageHeader
          title={`Good ${timeOfDayGreeting(new Date().getHours())}, ${firstName}`}
          subtitle={`Here's what's happening in ${workspaceName}.`}
        />
      )}

      {activeImports.length > 0 && (
        <div className="mt-8 flex items-center gap-3 rounded-xl border border-info-border bg-info-bg p-4">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-info" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-info">
              Importing chat history — {activeImports.length}{' '}
              {activeImports.length === 1 ? 'import' : 'imports'} running
            </p>
            <p className="text-[13px] text-info/80">
              Messages appear as indexing completes. You can keep using Brain.
            </p>
          </div>
        </div>
      )}

      {!connectionLoading && !connected && (
        <div className="mt-8 rounded-xl border border-border bg-card p-7">
          <div className="flex items-center gap-4">
            <IconTile tone="dark">
              <MessageSquare className="h-[18px] w-[18px]" />
            </IconTile>
            <div>
              <p className="font-semibold">Finish setting up Brain</p>
              <p className="text-[13px] text-muted-foreground">
                Connect WhatsApp or Signal to start indexing.
              </p>
            </div>
          </div>
          <Link to="/onboarding" className={cn(buttonVariants({ size: 'cta' }), 'mt-5')}>
            Continue setup
          </Link>
        </div>
      )}

      {/* Stat row */}
      <div className="mt-8 grid grid-cols-3 gap-4">
        {statsLoading ? (
          <>
            <Skeleton className="h-[124px] rounded-xl" />
            <Skeleton className="h-[124px] rounded-xl" />
            <Skeleton className="h-[124px] rounded-xl" />
          </>
        ) : (
          <>
            <StatCard label="Connected groups" value={groups.length} sub={groupsSub} />
            <StatCard label="Active API keys" value={enabledKeyCount} sub={keysSub} />
            <StatCard label="Messages indexed" comingSoon />
          </>
        )}
      </div>

      {/* Lower row */}
      <div className="mt-4 grid grid-cols-[1.4fr_1fr] items-start gap-4">
        <div className="rounded-xl border border-border p-7">
          <div className="flex items-center gap-4">
            <IconTile tone="dark">
              <Terminal className="h-[18px] w-[18px]" />
            </IconTile>
            <div>
              <p className="font-semibold">Connect an AI client</p>
              <p className="text-[13px] text-muted-foreground">
                Point Claude, Cursor, or any MCP client at your workspace.
              </p>
            </div>
          </div>
          <p className="mt-5 text-[13px] text-muted-foreground">
            Your workspace has a single MCP endpoint covering everything it indexes. Mint a key in
            Settings, then paste it with the endpoint URL into your client.
          </p>
          <Link
            to="/settings"
            className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-foreground hover:text-link"
          >
            Open Settings
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="rounded-xl border border-border p-7">
          <p className="font-semibold">Recent activity</p>
          <EmptyState
            icon={Activity}
            title="Activity feed coming soon"
            body="Indexing events will appear here."
          />
        </div>
      </div>
    </div>
  );
}
